"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ReadyForADeviceUpgrade() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="221995" id="ready-to-deploy" className="dark relative overflow-hidden bg-background py-24 text-foreground sm:py-32">
      <style>
        {`
          @keyframes panBackground {
            0% { background-position: 0px 0px; }
            100% { background-position: 100px 100px; }
          }
          .animate-circuit-pan {
            animation: panBackground 40s linear infinite;
          }
        `}
      </style>

      {}
      <div className="animate-circuit-pan absolute inset-0 opacity-10" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg stroke='%23a3e635' stroke-width='1' stroke-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3Ccircle cx='35' cy='33' r='1' fill='%23a3e635' fill-opacity='0.6'/%3E%3Ccircle cx='5' cy='33' r='1' fill='%23a3e635' fill-opacity='0.6'/%3E%3Ccircle cx='35' cy='3' r='1' fill='%23a3e635' fill-opacity='0.6'/%3E%3Ccircle cx='5' cy='3' r='1' fill='%23a3e635' fill-opacity='0.6'/%3E%3C/g%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background/95" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-default text-4xl font-bold tracking-tighter text-foreground sm:text-6xl lg:text-8xl uppercase">
            Need a Tech Tomorrow?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground font-default">VOLTEX provides rapid-response low-voltage and IT field services across Kansas City. From Connectivity, Mobility to Network and Communications, we ensure your infrastructure is built for performance.</p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-x-6">
            <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto rounded-sm font-default font-bold">
              <Link to="#">
                <RefreshCw className="mr-2 h-5 w-5" />
                Request Site Survey
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full border-border bg-white/5 text-white backdrop-blur-sm hover:bg-white/10 hover:text-white sm:w-auto rounded-sm font-default font-bold">
              <Link to="#">
                Schedule Service
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
          
          <div className="mt-12 flex items-center justify-center gap-x-8 text-xs font-mono uppercase tracking-widest text-muted-foreground sm:gap-x-12">
             <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-primary animate-pulse" data-ast-id="f:/src/pages/Home/sections/ReadyForADeviceUpgrade.tsx|sig:wbbqfg" data-ast-start="4668" data-ast-end="4733"></div>Currently Dispatching: KCMO REGION</div>
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                Overland Park
             </div>
             <div className="hidden items-center gap-2 sm:flex">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                Shawnee
             </div>
          </div>
        </div>
      </div>
    </section>;
}